import { useCallback } from 'react';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { upsertValuationPersonInputs } from '@dealstack/frontend/shared/dataExchange/services/valuations.service';
import { toastError } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { VALUATION_INPUT_DEBOUNCE_MS } from '@dealstack/shared/lib/debounceValues';
import {
	UpsertValuationPersonInputItemDto,
	ValuationPersonInputRes,
} from '@dealstack/shared/lib/generated';
import type {
	PendingPersonInputSave,
	PersonInputSaveResult,
} from '@dealstack/frontend/app/pages/Valuations/queries/usePersonInputsUpsert.types';
import { useDebouncedPendingSave } from '@dealstack/frontend/shared/hooks/useDebouncedPendingSave';
import { replaceCachePayload } from '@dealstack/frontend/shared/dataExchange/cacheHelpers';
import { VALUATION_SAVE_MUTATION_KEY } from '@dealstack/frontend/app/pages/Valuations/queries/queryKeys';
import {
	CATEGORY_TO_CLASSIFIER_MAP,
	PersonInputSavePayload,
} from '@dealstack/frontend/app/pages/Valuations/queries/personInputClassifiers';

interface UsePersonInputsUpsertArgs {
	dealId: string | undefined;
	valuationId: string | undefined;
	personId: string | undefined;
	mipId: string | undefined;
	queryKey: QueryKey;
	reloadSummary: () => Promise<unknown>;
}

export function usePersonInputsUpsert({
	dealId,
	valuationId,
	personId,
	mipId,
	queryKey,
	reloadSummary,
}: UsePersonInputsUpsertArgs) {
	const queryClient = useQueryClient();

	const { save, flush } = useDebouncedPendingSave<PendingPersonInputSave, PersonInputSaveResult>({
		mutationKey: [VALUATION_SAVE_MUTATION_KEY, valuationId ?? ''],
		debounceMs: VALUATION_INPUT_DEBOUNCE_MS,
		mutationFn: async ({ rows, target }) => {
			if (rows.length === 0 || !target.valuationId || !target.dealId || !target.personId) {
				return null;
			}

			const inputs: UpsertValuationPersonInputItemDto[] = rows
				.filter((row) => row.value.name?.trim())
				.map((row) => ({
					id: row.id,
					name: row.value.name,
					currency: row.value.value.currency,
					currencyAmount: Number(row.value.value.currencyAmount),
					classifierId: CATEGORY_TO_CLASSIFIER_MAP[row.categoryId] ?? row.categoryId,
				}));

			if (inputs.length === 0) {
				return null;
			}

			const response = await upsertValuationPersonInputs({
				valuationId: target.valuationId,
				dealId: target.dealId,
				personId: target.personId,
				mipId: target.mipId,
				data: { inputs },
			});
			return response.payload
				? { response: response.payload, queryKey: target.queryKey }
				: null;
		},
		onSuccess: async (result) => {
			if (!result) {
				return;
			}
			replaceCachePayload<ValuationPersonInputRes[]>(
				queryClient,
				result.queryKey,
				result.response.state
			);
			await reloadSummary();
		},
		onError: (error) => toastError('Error updating field', error),
	});

	// The rows belong to the entity on screen when they were edited, which the debounce can outlive.
	const saveFields = useCallback(
		(rows: PersonInputSavePayload[]) =>
			save({ rows, target: { dealId, valuationId, personId, mipId, queryKey } }),
		[save, dealId, valuationId, personId, mipId, queryKey]
	);

	return { saveFields, flush };
}
